/* The shell: header, footer and plain pages, from Main.dc.html and PhoneLibrary.dc.html. */
:root {
  --gutter: clamp(16px, 8.34vw, 120px);
  --gutter-header: clamp(16px, 4.45vw, 64px);
}

/* The wordmark */
.mark { display: flex; align-items: center; gap: 10px; text-decoration: none; color: var(--shell-ink); }
.mark--header .mark-name { font-weight: 800; font-size: 19px; letter-spacing: 0.01em; }
.mark--header .mark-sub { font-weight: 500; font-size: 15px; color: var(--shell-ink-62); }
.mark--phone { gap: 8px; min-height: 24px; }
.mark--phone .mark-name { font-weight: 800; font-size: 17px; }
.mark--phone .mark-sub { font-weight: 500; font-size: 14px; color: var(--shell-ink-62); }
.mark-dot { width: 5px; height: 5px; border-radius: 3px; background: var(--shell-gold); flex-shrink: 0; }

.icon-line { fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; }
.icon-arrow { fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; flex-shrink: 0; }

/* Laptop header */
.sh-header {
  height: 72px; display: flex; align-items: center; justify-content: space-between; gap: 24px;
  padding: 0 var(--gutter-header); border-bottom: 1px solid var(--shell-ink-10);
  position: relative; z-index: 5; background: var(--shell-bg);
}
.sh-search { position: relative; width: min(520px, 40vw); }
.sh-search-icon { position: absolute; left: 15px; top: 13px; color: var(--shell-ink-60); display: flex; }
.sh-search input {
  width: 100%; height: 44px; padding: 0 16px 0 44px; border-radius: 10px;
  border: 1px solid var(--shell-ink-16); background: var(--shell-ink-03); color: var(--shell-ink); font-size: 15px;
}
.sh-search input::placeholder, .ph-panel--search input::placeholder { color: var(--shell-ink-60); }
.sh-pop {
  position: absolute; background: var(--shell-bg); border: 1px solid var(--shell-ink-12);
  box-shadow: 0 18px 48px var(--shell-ink-14); padding: 8px; display: flex; flex-direction: column;
}
.sh-results { top: 52px; left: 0; right: 0; border-radius: 12px; gap: 2px; }
.sh-bell { top: 56px; right: 0; width: 400px; border-radius: 14px; }
.sh-pop-head {
  padding: 6px 12px 8px; font-size: 12px; font-weight: 700; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--shell-ink-60);
}
.sh-pop-head--bell { padding: 10px 12px; }
.sh-row { display: flex; flex-direction: column; gap: 3px; padding: 10px 12px; border-radius: 8px; text-decoration: none; color: var(--shell-ink); }
.sh-row--note { padding: 12px; }
.sh-row:hover { background: var(--shell-ink-04); }
.sh-row-label { font-size: 15px; font-weight: 600; }
.sh-row-label--note { line-height: 1.4; }
.sh-row-sub { font-size: 13px; color: var(--shell-ink-60); }
/* A search hit that opens a course recording, with its 16:9 frame in front (decision 47, 12 Oct): the
   thumbnail, or the grey panel with the date. Shared by the shell's 2 headers and a product's. Each
   selector names its row's own class as well, so it wins over .ph-row and .pp-pop-row (a column), which
   come later in the stylesheet. */
.sh-row.sh-row--thumb, .ph-row.ph-row--thumb, .pp-pop-row.pp-pop-row--thumb {
  display: grid; grid-template-columns: 80px minmax(0, 1fr); grid-template-rows: auto auto; column-gap: 12px; align-items: center;
}
.hit-thumb { grid-row: 1 / span 2; --rthumb-radius: 6px; }
/* too small for 3 lines: the date and the month */
.hit-thumb .rthumb-date { gap: 0; font-size: 9px; letter-spacing: 0.06em; }
.hit-thumb .rthumb-edge:first-child { display: none; }
.hit-thumb .rthumb-day { font-size: 15px; }
.sh-row--thumb .sh-row-label, .ph-row--thumb .ph-row-label, .pp-pop-row--thumb .pp-pop-label { align-self: end; }
.sh-row--thumb .sh-row-sub, .ph-row--thumb .ph-row-sub, .pp-pop-row--thumb .pp-pop-sub { align-self: start; }
.sh-none { padding: 12px; font-size: 14px; color: var(--shell-ink-60); }
.sh-actions { display: flex; align-items: center; gap: 14px; position: relative; }
.sh-round {
  width: 44px; height: 44px; border-radius: 22px; border: 1px solid var(--shell-ink-16);
  background: transparent; color: var(--shell-ink); display: flex; align-items: center; justify-content: center; position: relative;
}
.sh-unread { position: absolute; top: 9px; right: 10px; width: 8px; height: 8px; border-radius: 4px; background: var(--shell-gold); }
.sh-avatar {
  width: 44px; height: 44px; border-radius: 22px; border: 1.5px solid var(--shell-gold);
  display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 15px;
  color: var(--shell-ink); text-decoration: none;
}

/* Laptop footer */
.sh-footer {
  height: 72px; padding: 0 var(--gutter); display: flex; align-items: center; justify-content: space-between; gap: 24px;
  border-top: 1px solid var(--shell-ink-10); font-size: 13px; color: var(--shell-ink-60);
}
.sh-footer-links { display: flex; gap: 24px; align-items: center; }
.sh-footer-links a { color: var(--shell-ink-62); }

/* Phone header and its panels */
.ph-header {
  height: 60px; display: flex; align-items: center; justify-content: space-between;
  padding: 0 16px; border-bottom: 1px solid var(--shell-ink-10); background: var(--shell-bg);
}
.ph-actions { display: flex; align-items: center; gap: 8px; }
.ph-round {
  width: 40px; height: 40px; border-radius: 20px; border: 1px solid var(--shell-ink-16);
  background: transparent; color: var(--shell-ink); display: flex; align-items: center; justify-content: center; position: relative;
}
.ph-unread { position: absolute; top: 8px; right: 9px; width: 8px; height: 8px; border-radius: 4px; background: var(--shell-gold); }
.ph-avatar {
  width: 40px; height: 40px; border-radius: 20px; border: 1.5px solid var(--shell-gold);
  display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px;
  color: var(--shell-ink); text-decoration: none;
}
.ph-panel { border-bottom: 1px solid var(--shell-ink-10); display: flex; flex-direction: column; }
.ph-panel--search { padding: 12px 16px; gap: 8px; }
.ph-panel--search input {
  width: 100%; height: 44px; padding: 0 14px; border-radius: 10px; border: 1px solid var(--shell-ink-16);
  background: var(--shell-ink-04); color: var(--shell-ink); font-size: 16px;
}
.ph-panel--bell { padding: 8px 16px 12px; }
.ph-panel-head { padding: 8px 4px; font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--shell-ink-60); }
.ph-row {
  display: flex; flex-direction: column; gap: 2px; padding: 10px 4px; border-bottom: 1px solid var(--shell-ink-07);
  text-decoration: none; color: var(--shell-ink);
}
.ph-row-label { font-size: 15px; font-weight: 600; }
.ph-row-label--note { line-height: 1.4; }
.ph-row-sub { font-size: 13px; color: var(--shell-ink-60); }
.ph-none { padding: 10px 4px; font-size: 14px; color: var(--shell-ink-60); }

/* Phone footer */
.ph-footer {
  margin-top: 36px; padding: 22px 16px 28px; border-top: 1px solid var(--shell-ink-10);
  display: flex; flex-direction: column; gap: 10px; font-size: 13px; color: var(--shell-ink-60);
}
.ph-footer-links { display: flex; gap: 18px; flex-wrap: wrap; align-items: center; }
/* at least 24px to tap (the stage 5 batch, item 178) */
.ph-footer-links a { color: var(--shell-ink-62); display: inline-flex; align-items: center; min-height: 24px; }

/* Plain pages in the shell: holding pages, "nothing here", 2-step login */
.sh-page, .lib-page { min-height: 100vh; display: flex; flex-direction: column; background: var(--shell-bg); }
.sh-main { flex: 1; padding: 48px var(--gutter) 120px; display: flex; flex-direction: column; gap: 16px; max-width: 1200px; width: 100%; }
.sh-eyebrow { font-size: 13px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--shell-ink-60); font-weight: 700; }
.sh-h1 { margin: 0; font-size: 44px; font-weight: 800; letter-spacing: -0.01em; line-height: 1.1; }
.sh-lede { margin: 0; font-size: 17px; line-height: 1.55; color: var(--shell-ink-66); max-width: 640px; }
.sh-actions-row { margin: 8px 0 0; display: flex; gap: 12px; flex-wrap: wrap; }
.notice-actions { display: flex; margin-top: 20px; }

/* 2-step login */
.mfa-enrol { display: flex; flex-direction: column; gap: 12px; margin-top: 8px; }
.mfa-qr { width: 200px; height: 200px; border: 1px solid var(--shell-ink-12); border-radius: 12px; padding: 8px; background: var(--shell-bg); }
.mfa-key { margin: 0; font-size: 15px; line-height: 1.55; color: var(--shell-ink-66); max-width: 640px; }
.mfa-secret { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; color: var(--shell-ink); word-break: break-all; }
.mfa-form { display: flex; flex-direction: column; gap: 8px; max-width: 360px; margin-top: 8px; }
.mfa-form label { font-size: 14px; font-weight: 600; }
.mfa-code {
  height: 56px; padding: 0 16px; border-radius: 10px; border: 1px solid var(--shell-ink-18);
  font-size: 24px; letter-spacing: 0.3em; font-weight: 700; color: var(--shell-ink); background: var(--shell-bg);
}
.mfa-error { margin: 0; font-size: 15px; font-weight: 600; color: var(--shell-ink); }

@media (max-width: 760px) {
  .sh-main { padding: 28px 16px 48px; }
  .sh-h1 { font-size: 30px; }
}
